import { useQuery } from '@tanstack/react-query';
import {
  getHrOptions,
  getOccupations,
  getProfessions,
  resolveHrOptionGkey,
  searchProfessions,
  trackCommonStatistic,
} from '@/lib/api/common/options.api';
import { IOptionFilterType } from '@/shared';
import useDebounce from '@/hooks/useDebounce';

type UseOptionsParams = Omit<IOptionFilterType, 'gkey'> & {
  gkey: IOptionFilterType['gkey'] | string;
};

export function useCommonStatistic(key: string) {
  return useQuery({
    queryKey: ['options', 'statistic', key],
    queryFn: () => trackCommonStatistic(key),
    enabled: !!key,
    refetchOnWindowFocus: false,
  });
}

export function useOptions(params: UseOptionsParams) {
  const resolvedParams: IOptionFilterType = {
    ...params,
    gkey: resolveHrOptionGkey(params.gkey),
  };

  return useQuery({
    queryKey: ['options', resolvedParams],
    queryFn: () => getHrOptions(resolvedParams),
    enabled: !!params.gkey,
  });
}

export function useOccupations(branchId: number) {
  return useQuery({
    queryKey: ['occupations', branchId],
    queryFn: () => getOccupations(branchId),
    enabled: !!branchId,
  });
}

export function useProfessions(branchId: number) {
  return useQuery({
    queryKey: ['professions', branchId],
    queryFn: () => getProfessions(branchId),
    enabled: !!branchId,
  });
}

export function useSearchProfessions(search: string, limit = 10) {
  const debouncedSearch = useDebounce(search.trim(), 400);

  return useQuery({
    queryKey: ['search-professions', debouncedSearch, limit],
    queryFn: () =>
      searchProfessions({
        search: debouncedSearch,
        limit,
        withParent: true,
      }),
    enabled: debouncedSearch.length > 0,
  });
}
